/**
 * Meeting Section Selectors
 * Memoized selectors with defensive checks
 */

import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "@/store";
import { MeetingSectionComplement } from "@/types/meeting-section.types";

// Base selector
const selectMeetingSectionState = (state: RootState) => state.meetingSection;

// Select all sections
export const selectAllSections = createSelector(
  [selectMeetingSectionState],
  (meetingSection) => meetingSection?.sections || []
);

// Select loading state
export const selectSectionsLoading = createSelector(
  [selectMeetingSectionState],
  (meetingSection) => meetingSection?.loading || false
);

// Select error state
export const selectSectionsError = createSelector(
  [selectMeetingSectionState],
  (meetingSection) => meetingSection?.error || null
);

// Select current module ID
export const selectCurrentModuleId = createSelector(
  [selectMeetingSectionState],
  (meetingSection) => meetingSection?.currentModuleId || null
);

// Select preparation answers
export const selectPreparationAnswers = createSelector(
  [selectMeetingSectionState],
  (meetingSection) => meetingSection?.preparationAnswers || {}
);

// Select all complements sorted by sequence
export const selectAllComplements = createSelector(
  [selectAllSections],
  (sections) => {
    const complements: MeetingSectionComplement[] = [];
    sections.forEach((section) => {
      if (section.complements && Array.isArray(section.complements)) {
        complements.push(...section.complements);
      }
    });
    // Sort by sequence
    return complements.sort((a, b) => a.sequence - b.sequence);
  }
);

// Select model instructions from all sections
export const selectModelInstructions = createSelector(
  [selectAllSections],
  (sections) => {
    return sections
      .map((section) => section.instructions)
      .filter((instruction) => instruction && instruction.trim() !== "");
  }
);

// Select preparation progress (percentage of answered questions)
export const selectPreparationProgress = createSelector(
  [selectAllComplements, selectPreparationAnswers],
  (complements, answers) => {
    const totalQuestions = complements.length;
    if (totalQuestions === 0) return 0;

    const answeredCount = complements.filter(
      (complement) =>
        answers[complement.id] && answers[complement.id].trim() !== ""
    ).length;

    return Math.round((answeredCount / totalQuestions) * 100);
  }
);

// Check if all preparation questions are answered
export const selectAllQuestionsAnswered = createSelector(
  [selectAllComplements, selectPreparationAnswers],
  (complements, answers) => {
    if (complements.length === 0) return false;

    return complements.every(
      (complement) =>
        answers[complement.id] && answers[complement.id].trim() !== ""
    );
  }
);

// Select specific section by ID
export const selectSectionById = (sectionId: string) =>
  createSelector([selectAllSections], (sections) =>
    sections.find((section) => section.id === sectionId)
  );

// Select specific complement by ID
export const selectComplementById = (complementId: string) =>
  createSelector([selectAllComplements], (complements) =>
    complements.find((complement) => complement.id === complementId)
  );
